import React, { useCallback } from "react";
import { useFormikContext } from "formik";
import { useApi } from "../../../api/useApi";
import DataTable from "../../Table/DataTable";
import { groups } from "../../../api/groupQueries";
import styled from "styled-components";
import { addGroup, removeGroup } from "../../../api/workgroupMutations";
import TableButton from "../../Button/TableButton";
import { FormControl } from "../../Form/FormControls";
import ErrorMessages from "../../Notifications/ErrorMessages";
import { useSearch } from "../../../Hooks/useSearch";
import SearchInput from "../Search/SearchInput";

const SingleRowFlex = styled.div`
  display: flex;
  align-items: center;
`;

const DisplayName = styled.div`
  flex: 1;
`;

const StaticGroupSelect = ({ updateWorkGroup, workGroupId }) => {
  const {
    values: { groups: selectedItems },
  } = useFormikContext();

  const [{ errors, loading, data }, doFetch] = useApi();

  const availGroups = data?.groups?.edges ?? [];
  const totalCount = data?.groups?.totalCount;
  const pageInfo = data?.groups?.pageInfo;

  //Fetch for Table Paged
  const fetchData = React.useCallback(
    ({ pageSize, cursor }) => {
      doFetch({
        query: groups,
        variables: {
          first: pageSize,
          after: cursor,
          order: {
            displayName: "ASC",
          },
        },
      });
    },
    [doFetch]
  );

  const onSearch = useCallback(
    (text) => {
      if (text) {
        doFetch({
          query: groups,
          variables: {
            where: {
              or: [{ displayName: { contains: text } }],
            },
            order: [{ displayName: "ASC" }],
          },
        });
      } else {
        doFetch({
          query: groups,
          variables: {
            order: { displayName: "ASC" },
            first: 50,
          },
        });
      }
    },
    [doFetch]
  );

  const { inputText, handleInputChange, tooShortWarning } = useSearch({
    onSearch,
  });

  const addGroupId = (original) => {
    const variables = {
      workGroupId: workGroupId,
      orgGroupId: original?.node?.id,
    };
    updateWorkGroup({ query: addGroup, variables: variables });
  };

  const removeSelection = (original) => {
    const variables = {
      workGroupId: workGroupId,
      groupId: original?.node?.id,
    };
    updateWorkGroup({ query: removeGroup, variables: variables });
  };

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.displayName,
      Cell: ({ row: { original } }) => {
        const existingIndex = selectedItems.findIndex(
          (item) => item?.id === original?.node?.id
        );

        return (
          <SingleRowFlex>
            <DisplayName>{original?.node?.displayName}</DisplayName>{" "}
            {existingIndex === -1 ? (
              <TableButton type="button" onClick={() => addGroupId(original)}>
                +
              </TableButton>
            ) : (
              <TableButton
                type="button"
                danger={true}
                onClick={() => removeSelection(original)}
              >
                x
              </TableButton>
            )}
          </SingleRowFlex>
        );
      },
    },
  ];

  return (
    <>
      <FormControl style={{ maxWidth: "300px" }}>
        <SearchInput
          label="Search Group"
          placeholder="Search Group"
          inputText={inputText}
          handleChange={handleInputChange}
          warning={tooShortWarning}
        />

        {errors ? <ErrorMessages errors={errors} /> : null}
      </FormControl>

      <DataTable
        // Server query has no sort wiring, so a clickable header would
        // paint a sort arrow without reordering rows (AC-15839).
        disableSortBy
        fetchData={fetchData}
        loading={loading}
        pageInfo={pageInfo}
        totalCount={totalCount}
        data={availGroups}
        columns={columnsData}
        defaultPageSize={50}
      />
    </>
  );
};

const SelectOrganizationGroup = React.memo(
  ({ updateWorkGroup, workGroupId }) => {
    return (
      <>
        <StaticGroupSelect
          updateWorkGroup={updateWorkGroup}
          workGroupId={workGroupId}
        />
      </>
    );
  }
);

export default SelectOrganizationGroup;
